/**
 * Consent bar offset — publishes the cookie banner's height as `--consent-bar-h`.
 * ---------------------------------------------------------------------------
 * Consentmo's banner is `position: fixed` at the bottom of the viewport with
 * z-index 2147483647, so it lies on top of whatever the page has down there:
 * the footer's legal row, the back-to-top rail, the cart drawer's checkout
 * button, the loyalty pop-up. None of those can see it — it lives in a shadow
 * root on <csm-cookie-consent>, so no page stylesheet can measure or target it.
 *
 * This script is the bridge. It measures the bar and writes its height to
 * `--consent-bar-h` on <html>, plus a `consent-bar-open` class for rules that
 * need to change more than a length. the inline style block in snippets/consent-bar-offset.liquid spends both.
 * With no bar the variable is `0px`, so every `calc()` that references it
 * collapses back to its original value — the offsets cost nothing on the vast
 * majority of page views, where consent has already been given.
 *
 * WHY MEASURE RATHER THAN HARD-CODE
 * The bar is text, and its height depends on how that text wraps: 125px on a
 * 2560px viewport, considerably more on a phone, and it changes again whenever
 * the copy is edited in the Consentmo admin. A fixed number would be wrong the
 * day someone adds a sentence.
 *
 * WHAT COUNTS AS "THE BAR"
 * `.csm-cmp-bar` is the banner; the preferences dialog is a different node in
 * the same shadow root and must not be measured — it is a centred modal, not
 * something the page needs to make room for. The bottom-parked check also means
 * a top-positioned banner (Consentmo's `cc-top` layout) correctly reports 0:
 * nothing at the bottom of the page is covered by it.
 *
 * WHY THE INLINE-STYLE PATH EXISTS AT ALL
 * An app that writes an important `bottom` to its own style attribute outranks
 * every stylesheet, so the only way to move it is to write the style attribute
 * too — and to write it again when the app rewrites it. Its resting offset is
 * read rather than hard-coded, since that value is the app's to choose. Rebuy's
 * cart flyout was the case this was built for; that cart is gone and NUDGE is
 * empty now, but the mechanism stays for the next app that behaves this way.
 * The native cart drawer needs none of it — it is offset from the stylesheet.
 *
 * Re:amaze needed the opposite treatment. It rewrites its style attribute
 * continuously, so no inline write survives; it is moved with a `transform`
 * from the stylesheet instead, which it never touches.
 *
 * Paired with snippets/consent-bar-offset.liquid.
 */
(function (w, d) {
  'use strict';

  var VAR = '--consent-bar-h';
  var OPEN_CLASS = 'consent-bar-open';
  var root = d.documentElement;
  var last = null;
  var watchedBar = null;
  var barResizeObserver = null;

  function shadow() {
    var host = d.querySelector('csm-cookie-consent');
    return host && host.shadowRoot;
  }

  function findBar() {
    var sr = shadow();
    if (!sr) return null;
    // `.cc-window` is the app's older alias for the same node; the dialog
    // carries `.cc-settings-view` instead, so it can never match here.
    return sr.querySelector('.csm-cmp-bar') || sr.querySelector('.cc-window');
  }

  function heightOf(el) {
    if (!el) return 0;

    var cs = w.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.position !== 'fixed') return 0;
    if (parseFloat(cs.opacity) === 0) return 0;

    var rect = el.getBoundingClientRect();
    if (rect.height < 1) return 0;

    // Only reserve space for a bar actually parked against the bottom edge.
    // A couple of pixels of slack for sub-pixel layout and browser zoom.
    if (rect.bottom < w.innerHeight - 2) return 0;

    return Math.ceil(rect.height);
  }

  /* ------------------------------------------- widgets CSS cannot reach ---
    These position themselves with an important INLINE `bottom`. Nothing in a
    stylesheet can outrank that, so the offset has to go into the style
    attribute too — and go back in when the app rewrites it, which both of
    these do on their own schedule.
  */
  var NUDGE = [
    // Empty on purpose, and the machinery below stays for the next app that
    // writes an important inline `bottom` of its own.
    //
    // Rebuy's cart flyout was the last entry here. That cart no longer renders,
    // and the native drawer needs no JS nudge — it is offset from the
    // stylesheet (see `.consent-bar-open .drawer.drawer--is-open` in the CSS).
    //
    // Re:amaze's chat bubble was here too and is not any more: it rewrites its
    // own style attribute continuously, so the offset never survived. It is
    // moved with a `transform` from the stylesheet instead.
  ];

  function nudge(entry, h) {
    var el = d.querySelector(entry.selector);
    if (!el) return;

    if (el !== entry.el) {
      entry.el = el;
      entry.base = null;
      entry.written = null;
      entry.rewrites = 0;
      new MutationObserver(function () {
        nudge(entry, last || 0);
      }).observe(el, { attributes: true, attributeFilter: ['style'] });
    }

    var cur = el.style.getPropertyValue('bottom');

    /*
      Anything we did not write ourselves is the app's own resting value and
      becomes the base to offset from. Doubles as the re-entry guard: our own
      writes match `written` and fall straight through.

      Inline only — deliberately no computed-style fallback. An off-canvas
      drawer reports a used `bottom` of ~1260px, and taking that as the base
      produced `bottom: calc(1260px + 126px)`, which against `top: 0` resolves
      to a negative height and collapses the drawer to nothing.
    */
    if (cur !== entry.written) {
      entry.base = cur || '0px';
      entry.basePriority = el.style.getPropertyPriority('bottom');
      if (entry.written) entry.rewrites++;
    }

    if (h > 0) {
      // If an app insists on rewriting after every one of our writes, stand
      // down rather than trade style attributes with it forever. Checked only
      // on the way in: standing down must never skip the cleanup below, or the
      // widget would be left wearing our values with no bar to justify them.
      if (entry.rewrites > 10) return;

      var base = entry.base && entry.base !== '0px' ? entry.base : null;
      var want = base ? 'calc(' + base + ' + ' + h + 'px)' : h + 'px';
      if (cur === want && (!entry.releaseHeight || el.style.getPropertyValue('height') === 'auto')) return;
      entry.written = want;
      el.style.setProperty('bottom', want, 'important');
      if (entry.releaseHeight) el.style.setProperty('height', 'auto', 'important');
      return;
    }

    if (!entry.written) return;
    // Hand the widget back exactly as its app left it.
    entry.written = null;
    entry.rewrites = 0;
    el.style.removeProperty('bottom');
    if (entry.releaseHeight) el.style.removeProperty('height');
    if (entry.base) el.style.setProperty('bottom', entry.base, entry.basePriority);
  }

  /* ------------------------------------------- chat's resting clearance ---
    Re:amaze is moved with a `transform` rather than a `bottom` (see the
    stylesheet), so the lift has to make up only the difference between the
    bar's height and the clearance the widget already gives itself. That
    clearance is the app's to choose, and writing it into the stylesheet did
    not hold: the bubble rested 75px above the bottom edge on 2026-08-27 and
    22px above it on 2026-09-08, which left the lift 53px short and the bubble
    behind the bar. Measure it instead, and publish it for the CSS to spend.

    `bottom` is read from the computed style, which a transform does not touch,
    so the value stays put once the lift is applied — no feedback loop. A
    number outside a sane range means the widget is positioned some other way
    or is parked off-canvas; leave the stylesheet's fallback in place rather
    than acting on it. The panel only exists while the chat is open, so both
    are re-read on every measurement.
  */
  var CHAT_REST = [
    { selector: '#reamaze-widget', varName: '--chat-rest-bubble' },
    { selector: '#reamaze-wrapper', varName: '--chat-rest-panel' }
  ];
  var CHAT_REST_MAX = 200;

  function publishChatRest() {
    CHAT_REST.forEach(function (entry) {
      var el = d.querySelector(entry.selector);
      if (!el) return;

      var rest = parseFloat(w.getComputedStyle(el).bottom);
      if (!isFinite(rest) || rest < 0 || rest > CHAT_REST_MAX) return;

      var want = Math.round(rest) + 'px';
      if (want === entry.written) return;
      entry.written = want;
      root.style.setProperty(entry.varName, want);
    });
  }

  /* --------------------------------------------------------------- publish */

  function publish() {
    var bar = findBar();
    var h = heightOf(bar);

    if (bar !== watchedBar) {
      watchedBar = bar;
      if (barResizeObserver) barResizeObserver.disconnect();
      // The bar grows and shrinks as its text rewraps; watch the element
      // itself rather than re-measuring on a timer.
      if (bar && w.ResizeObserver) {
        barResizeObserver = new w.ResizeObserver(function () {
          publish();
        });
        barResizeObserver.observe(bar);
      }
    }

    if (h !== last) {
      // The body padding is added BELOW the footer, so it grows the document
      // rather than moving anything up. A visitor already parked at the bottom
      // of the page — exactly where the covered legal row is — would see the
      // row stay put and the new space open up out of sight beneath them.
      // Take the scroll position with it so their view does not change.
      var atBottom = w.innerHeight + w.pageYOffset >= d.documentElement.scrollHeight - 2;
      var delta = h - (last || 0);

      last = h;
      root.style.setProperty(VAR, h + 'px');
      if (h > 0) root.classList.add(OPEN_CLASS);
      else root.classList.remove(OPEN_CLASS);

      if (atBottom && delta > 0) w.scrollBy(0, delta);
    }

    // Runs every time, not just on change: these widgets may only just have
    // been rendered, long after the height settled.
    publishChatRest();
    NUDGE.forEach(function (entry) {
      nudge(entry, h);
    });
  }

  // Coalesce the several signals below into one measurement.
  //
  // setTimeout, not requestAnimationFrame: rAF is frozen in a backgrounded tab,
  // and a visitor who opens the site in a background tab and comes back to it
  // would find the offsets never applied. getBoundingClientRect forces layout
  // anyway, so there is nothing to gain by waiting for a frame.
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    w.setTimeout(function () {
      queued = false;
      publish();
    }, 0);
  }

  function watchShadow() {
    var sr = shadow();
    if (!sr) return false;
    // The banner is added, removed and class-toggled inside the shadow root;
    // attributes matter because the app hides it by class before removing it.
    new MutationObserver(schedule).observe(sr, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
    schedule();
    return true;
  }

  // <csm-cookie-consent> is injected by the app's script, so it is usually not
  // in the DOM yet. Poll briefly for the host, then hand over to the observer.
  if (!watchShadow()) {
    var tries = 0;
    var poll = setInterval(function () {
      if (watchShadow() || ++tries > 150) clearInterval(poll);
    }, 200);
  }

  w.addEventListener('resize', schedule);
  w.addEventListener('orientationchange', schedule);
  ['consentmoSignal', 'consentmoSignal_onLoad', 'consentmo:dialogClosed', 'visitorConsentCollected'].forEach(
    function (name) {
      d.addEventListener(name, schedule);
    }
  );
})(window, document);
